import {
  SearchableCard,
  SearchableCardOption,
  useModalPortalTarget,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { RefObject } from "react";
import { CopyIcon } from "../../../svg/copy-icon";

/** Props for a single labeled SearchableCard row inside the Site NPI modal. */
export interface SingleInputFieldProps {
  /** Left-column label text (e.g. "Site NPI"). */
  label: string;
  /** Currently selected option value. */
  value: string;
  /** Dropdown options for the searchable card. */
  options: SearchableCardOption[];
  /** Called when the user selects or creates an option. */
  onChange: (value: string, option: SearchableCardOption) => void;
  /** Called once focus leaves the entire searchable card control. */
  onBlur?: () => void;
  /** Stable id forwarded to SearchableCard for a11y and form wiring. */
  id?: string;
  /** Trigger placeholder when no value is selected. */
  placeholder?: string;
  /** Placeholder for the in-dropdown search input. */
  searchPlaceholder?: string;
  /** Validation error message shown below the control. */
  error?: string;
  /** Helper text shown below the control. */
  hint?: string;
  /** Marks the field as required in the UI. */
  required?: boolean;
  /** Disables user interaction. */
  disabled?: boolean;
  /** Shows a loading state while options are being fetched. */
  loading?: boolean;
  /** Message displayed while loading. */
  loadingMessage?: string;
  /** Called when the user types in the search input (async filtering). */
  onSearchChange?: (searchTerm: string) => void;
  /** Called when the user creates a new option from search text. */
  onCreateOption?: (value: string) => void;
}

/**
 * Renders one labeled row with a SearchableCard for the Site NPI modal.
 *
 * Portal targeting is handled internally via {@link useModalPortalTarget} so
 * the dropdown renders above the modal overlay. When a non-empty value is
 * selected, a copy icon writes that value to the clipboard. Parent components
 * own value, options, validation, and change handlers.
 *
 * @param props - Label, value, options, and SearchableCard callbacks.
 * @returns A labeled searchable dropdown row with an optional copy affordance.
 */
const SingleInputField = ({
  label,
  value,
  options,
  onChange,
  onBlur,
  id,
  placeholder,
  searchPlaceholder,
  error,
  hint,
  required,
  disabled,
  loading,
  loadingMessage,
  onSearchChange,
  onCreateOption,
}: SingleInputFieldProps) => {
  const portalTarget = useModalPortalTarget();

  return (
    <div className="single-input-field--container flex w-full items-center gap-2 border-b border-dashed border-primaryGray-15 py-2">
      <div className="single-input--label w-1/4 shrink-0 text-tiny font-semibold text-primaryGray-2">
        {label} :
      </div>
      <div
        className="single-input--searchable-card flex w-3/4 min-w-0 items-start gap-2"
        onBlur={(event) => {
          if (
            !event.currentTarget.contains(event.relatedTarget as Node | null)
          ) {
            onBlur?.();
          }
        }}
      >
        <SearchableCard
          id={id}
          label=""
          options={options}
          value={value}
          defaultValue={value}
          placeholder={placeholder}
          searchPlaceholder={searchPlaceholder}
          error={error}
          hint={hint}
          required={required}
          disabled={disabled}
          loading={loading}
          loadingMessage={loadingMessage}
          size="small"
          className="min-w-0 flex-1"
          shouldUsePortal={false}
          onChange={onChange}
          onSearchChange={onSearchChange}
          onCreateOption={onCreateOption}
          portalTarget={portalTarget as RefObject<HTMLElement>}
          keepTriggerMounted
        />
        {value && value.trim().length > 0 && (
          <CopyIcon
            width="14px"
            height="14px"
            className="shrink-0 cursor-pointer self-center"
            onClick={() => {
              navigator.clipboard.writeText(value);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default SingleInputField;
